import { useCallback, useEffect, useRef } from 'react'
import type { MutableRefObject } from 'react'
import { useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import moment from 'moment'
import {
  getCodeOptions,
  getTimeEntry,
  getTimeEntries,
  getTimeSlots,
} from '../../../../api/commonApi'
import {
  roundDurationToNearestUnitInSeconds,
  toLocalEndOfDay,
  toLocalStartOfDay,
} from '../../../matteroomTab/Utils'
import { getItemSourceIcons } from '../../../components/common/sourceIcons'
import { LOCAL_AUTH_STORAGE_KEYS } from '../../auth/constants'
import { useTimeEntriesStore } from '../../../stores/useTimeEntriesStore'
import { useLoadingStore } from '../../../stores/useLoadingStore'
import { useWorkspaceUiStore } from '../../../stores/useWorkspaceUiStore'
import { useUserStore } from '../../../stores/useUserStore'
import { getFilterDateRange } from '../utils/dateRange'
import { buildAssetUrl } from '../../../utils/assetUrl'
import { ROUTER_PATH } from '../../router/config/constants'
import { getPageForPathname } from '../../router/utils/get-page-for-pathname'
import { useDeviceStore } from '../../../stores/useDeviceStore'
import { countSuggestGroups } from '../utils/timeEntryViewUtils'
import { markTimeEntryAsReleased } from '../utils/timeEntryWebpush'

type UseTimeEntriesOptions = {
  hasCheckedHeartbeatOnceRef: MutableRefObject<boolean>
  setHasCheckedHeartbeatOnce: (checked: boolean) => void
  updateAliveApps: () => void
}

export const useTimeEntries = ({
  hasCheckedHeartbeatOnceRef,
  setHasCheckedHeartbeatOnce,
  updateAliveApps,
}: UseTimeEntriesOptions) => {
  const isMobile = useDeviceStore(state => state.isMobile)
  const queryClient = useQueryClient()
  const location = useLocation()
  const currentPage =
    location.pathname === ROUTER_PATH.help
      ? 'help'
      : getPageForPathname(location.pathname)
  const setShowWelcome = useWorkspaceUiStore(state => state.setShowWelcome)
  const { setLoading, setMagicLoading } = useLoadingStore()
  const selectedUser = useUserStore(state => state.selectedUser)
  const {
    filter,
    selectedWeekDay,
    jsonData,
    step,
    codes,
    minMinutes,
    minDailyWorkingHours,
    setFilter,
    setWeekData,
    setSelectedWeekDay,
    setJsonData,
    setRefreshFlag,
    setTimeEntriesCount,
    setStep,
    setWeekLoggedHours,
    setCodes,
  } = useTimeEntriesStore()

  const codesRef = useRef(codes)
  const selectedUserRef = useRef(selectedUser)
  const slotsByEntryIdRef = useRef<Record<string, any[]>>({})
  const entryRefreshStateRef = useRef<Record<string, any>>({})

  useEffect(() => {
    codesRef.current = codes
  }, [codes])

  useEffect(() => {
    selectedUserRef.current = selectedUser
  }, [selectedUser])

  useEffect(() => {
    if (isMobile) {
      setStep(0)
    }
  }, [filter, isMobile, setStep])

  const getWeekLoggedHours = useCallback((list: any[]) => {
    const now = moment(new Date())
    let hours = 0
    const startOfWeek = now.clone().startOf('week')
    const endOfWeek = now.clone().endOf('week')

    list.forEach(item => {
      const itemDate = moment(item.date)
      if (
        itemDate.isBetween(startOfWeek, endOfWeek, 'day', '[]') &&
        (item.status == 'fetched' || item.status == 'created')
      ) {
        hours += item.duration
      }
    })

    return hours
  }, [])

  const getWeekData = useCallback(
    (minDailyWorkingHoursValue: any, timeEntries: any[], options: any = {}) => {
      const { preserveSelectedWeekDay = true } = options

      if (['settings', 'help'].includes(currentPage)) {
        return
      }

      const currentWeekStart = moment().startOf('week')
      const enumWeekKey = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      const start = moment(currentWeekStart).add(7 * step, 'days')
      const array: any[] = []

      for (let index = 0; index < 7; index++) {
        array.push({
          key: enumWeekKey[index],
          date: moment(start).add(index, 'days'),
          mappingDate: moment(moment(start).add(index, 'days')).format(
            'YYYY-MM-DD'
          ),
          format: moment(moment(start).add(index, 'days')).format('YYYY/MM/DD'),
          color: '#999',
          released_seconds: 0,
        })
      }

      const allowedStatuses = new Set(['released'])
      const today = moment().startOf('day')

      array.forEach(arr => {
        let released_seconds = 0

        timeEntries.forEach(item => {
          if (
            moment(item.date).isSame(arr.date, 'day') &&
            allowedStatuses.has(item.status)
          ) {
            released_seconds += item.duration
          }
        })

        arr.released_seconds = released_seconds

        if (arr.date.isAfter(today)) {
          arr.color = '#999'
          arr.background = '#F5F5F5'
        } else if (['Sun', 'Sat'].indexOf(arr.key) === -1) {
          if (released_seconds < minDailyWorkingHoursValue * 3600) {
            arr.color = '#CA2921'
            arr.background = '#FCEFEE'
          } else {
            arr.color = '#007166'
            arr.background = '#F2F8F7'
          }
        } else {
          arr.color = '#525252'
          arr.background = '#F5F5F5'
        }
      })

      setWeekData(array)

      if (!preserveSelectedWeekDay) {
        if (!selectedWeekDay) {
          const currentDay = moment(new Date()).format('YYYY/MM/DD')
          const find = array.find(ele => ele.format === currentDay)
          if (find) setSelectedWeekDay(find)
        } else {
          const find = array.find(ele => ele.key === selectedWeekDay.key)
          if (find && find.mappingDate !== selectedWeekDay.mappingDate) {
            setSelectedWeekDay(find)
          }
        }
      }
    },
    [currentPage, selectedWeekDay, setSelectedWeekDay, setWeekData, step]
  )

  const getTimeEntriesCount = useCallback(
    (timeEntries: any[]) => {
      if (filter == 3) {
        return
      }

      const now = moment(new Date())
      const filteredEntries: any[] = []
      const getEntryDate = (item: any) =>
        item?.date || item?.dt || item?.start || item?.stop

      if (filter == 1) {
        const startOfMonth = now.clone().add(step, 'months').startOf('month')
        const endOfMonth = now.clone().add(step, 'months').endOf('month')
        timeEntries.forEach(item => {
          const itemDate = moment(getEntryDate(item))
          if (
            itemDate.isValid() &&
            itemDate.isBetween(startOfMonth, endOfMonth, 'day', '[]')
          ) {
            filteredEntries.push(item)
          }
        })
      } else if (filter == 2) {
        const startOfWeek = now.clone().add(step, 'weeks').startOf('week')
        const endOfWeek = now.clone().add(step, 'weeks').endOf('week')
        timeEntries.forEach(item => {
          const itemDate = moment(getEntryDate(item))
          if (
            itemDate.isValid() &&
            itemDate.isBetween(startOfWeek, endOfWeek, 'day', '[]')
          ) {
            filteredEntries.push(item)
          }
        })
      } else {
        timeEntries.forEach(item => {
          const itemDate = moment(getEntryDate(item))
          const itemDateMoment = itemDate.isValid()
            ? itemDate.format('YYYY-MM-DD')
            : null
          if (
            selectedWeekDay &&
            itemDateMoment === selectedWeekDay.mappingDate
          ) {
            filteredEntries.push(item)
          }
        })
      }

      setTimeEntriesCount(countSuggestGroups(filteredEntries))
    },
    [filter, selectedWeekDay, setTimeEntriesCount, step]
  )

  const normalizeTimeEntry = useCallback((item: any, minMinutesValue: any) => {
    item.date = moment(item.dt).format('YYYY-MM-DD')

    if (minMinutesValue && item.duration) {
      item.duration = roundDurationToNearestUnitInSeconds(
        item.duration,
        minMinutesValue
      )
    }

    item.matter_number = item.matter_number || null
    item.matter_name = item.matter_name || null
    item.client_number = item.client_number || null
    item.client_name = item.client_name || null
    item.updated_by_avatar = buildAssetUrl(item.updated_by_avatar)

    const entrySlots = slotsByEntryIdRef.current[item.id] || []
    item.source_icons = getItemSourceIcons({ ...item, slots: entrySlots })
    item.time = moment(item.start ? item.start : item.stop).format('H:00')

    return item
  }, [])

  const getLoginUser = useCallback(() => {
    try {
      return JSON.parse(
        localStorage.getItem(LOCAL_AUTH_STORAGE_KEYS.user) || 'null'
      )
    } catch {
      return null
    }
  }, [])

  const getDelegatorsQuerySuffix = useCallback(() => {
    const loginUser = getLoginUser()
    const user = selectedUserRef.current
    if (loginUser?.id && user?.id && +user.id !== +loginUser.id) {
      return `&delegators=${user.id}`
    }
    return ''
  }, [getLoginUser])

  const loadTimeEntries = useCallback(
    async (
      minMinutesValue: any,
      minDailyWorkingHoursValue: any,
      { refreshQueryData = true }: { refreshQueryData?: boolean } = {}
    ) => {
      if (!(codesRef.current && Object.keys(codesRef.current).length)) {
        const codeOptions = await getCodeOptions()

        ;['TASKCODE', 'ACTIVITYCODE'].forEach(type => {
          if (Array.isArray(codeOptions[type])) {
            codeOptions[type].forEach((ele: any) => {
              if (!ele.origin_name) {
                if (ele.name.startsWith(ele.code + ' ')) {
                  ele.origin_name = ele.name.substring(ele.code.length + 1)
                } else {
                  ele.origin_name = ele.name
                }
              }
              ele.name = ele.code + ' ' + ele.origin_name
            })
          }
        })

        setCodes(codeOptions)
        codesRef.current = codeOptions
      }

      const loginUser = getLoginUser()
      const { startDate, endDate } = getFilterDateRange(filter, step)
      const query =
        loginUser &&
        selectedUser &&
        selectedUser.id &&
        loginUser.id &&
        selectedUser.id !== loginUser.id
          ? `?delegators=${selectedUser.id}&start_date=${toLocalStartOfDay(startDate)}&end_date=${toLocalEndOfDay(endDate)}`
          : `?start_date=${toLocalStartOfDay(startDate)}&end_date=${toLocalEndOfDay(endDate)}`

      let timeEntries = await getTimeEntries(query)
      timeEntries = timeEntries.filter((ele: any) => ele.slot_ids.length > 0)

      const allEntryIds = timeEntries
        .map((entry: any) => entry.id)
        .filter(Boolean)
      const allSlots =
        allEntryIds.length > 0
          ? await getTimeSlots(
              `?timeentry_ids=${allEntryIds.join(',')}${getDelegatorsQuerySuffix()}`
            )
          : []
      const slotsMap: Record<string, any[]> = {}
      allSlots.forEach((slot: any) => {
        if (!slotsMap[slot.timeentry]) slotsMap[slot.timeentry] = []
        slotsMap[slot.timeentry].push(slot)
      })
      slotsByEntryIdRef.current = slotsMap

      timeEntries = timeEntries
        .map((item: any) => normalizeTimeEntry(item, minMinutesValue))
        .filter((item: any) => {
          return (
            item.matter_number &&
            item.matter_number.length > 0 &&
            item.client_number &&
            item.client_number.length > 0
          )
        })

      setJsonData({ datas: timeEntries })
      setWeekLoggedHours(getWeekLoggedHours(timeEntries))
      getTimeEntriesCount(timeEntries)
      getWeekData(minDailyWorkingHoursValue, timeEntries, {
        preserveSelectedWeekDay: false,
      })

      setLoading(false)
      setMagicLoading(false)

      if (refreshQueryData) {
        setRefreshFlag(prev => prev + 1)
      }

      if (hasCheckedHeartbeatOnceRef.current) {
        updateAliveApps()
      }

      setHasCheckedHeartbeatOnce(true)

      if (
        localStorage.getItem(LOCAL_AUTH_STORAGE_KEYS.firstLogin) === 'true' &&
        !isMobile
      ) {
        localStorage.removeItem(LOCAL_AUTH_STORAGE_KEYS.firstLogin)
        setShowWelcome(true)
      }
    },
    [
      filter,
      getTimeEntriesCount,
      getWeekData,
      getWeekLoggedHours,
      hasCheckedHeartbeatOnceRef,
      normalizeTimeEntry,
      getLoginUser,
      getDelegatorsQuerySuffix,
      selectedUser?.id,
      setCodes,
      setHasCheckedHeartbeatOnce,
      setJsonData,
      setLoading,
      setMagicLoading,
      setRefreshFlag,
      setShowWelcome,
      setWeekLoggedHours,
      step,
      updateAliveApps,
    ]
  )

  const updateTimeEntriesByDate = useCallback(
    (
      prevTimeEntries: any[],
      newTimeEntries: any[],
      date: string,
      minMinutesValue: any
    ) => {
      const normalizedNew = newTimeEntries
        .filter(entry => entry.slot_ids?.length > 0)
        .map(item => normalizeTimeEntry(item, minMinutesValue))

      const filteredPrev = prevTimeEntries.filter(item => item.date !== date)

      return [...filteredPrev, ...normalizedNew]
    },
    [normalizeTimeEntry]
  )

  const applyTimeEntryPatch = useCallback(
    (
      newEntries: any[],
      date: string,
      minMinutesValue: any,
      minDailyWorkingHoursValue: any
    ) => {
      setJsonData(prevJsonData => {
        const prevDatas = prevJsonData.datas || []
        const updated = updateTimeEntriesByDate(
          prevDatas,
          newEntries,
          date,
          minMinutesValue
        )

        setWeekLoggedHours(getWeekLoggedHours(updated))
        getTimeEntriesCount(updated)
        getWeekData(minDailyWorkingHoursValue, updated, {
          preserveSelectedWeekDay: true,
        })

        return {
          ...prevJsonData,
          datas: updated,
        }
      })
    },
    [
      getTimeEntriesCount,
      getWeekData,
      getWeekLoggedHours,
      setJsonData,
      setWeekLoggedHours,
      updateTimeEntriesByDate,
    ]
  )

  const removeEntryFromGrid = useCallback(
    (timeentryId: any) => {
      setJsonData(prev => {
        const prevDatas = prev.datas || []
        if (!prevDatas.some(item => item.id === timeentryId)) return prev

        const updated = prevDatas.filter(item => item.id !== timeentryId)
        setWeekLoggedHours(getWeekLoggedHours(updated))
        getTimeEntriesCount(updated)
        getWeekData(minDailyWorkingHours, updated, {
          preserveSelectedWeekDay: true,
        })

        return { ...prev, datas: updated }
      })

      delete slotsByEntryIdRef.current[timeentryId]
    },
    [
      getTimeEntriesCount,
      getWeekData,
      getWeekLoggedHours,
      minDailyWorkingHours,
      setJsonData,
      setWeekLoggedHours,
    ]
  )

  const markEntryAsReleased = useCallback(
    (timeentryId: any) => {
      queryClient.setQueriesData(
        { queryKey: ['daily-time-entries'] },
        (entries: unknown) =>
          Array.isArray(entries)
            ? markTimeEntryAsReleased(entries, timeentryId)
            : entries
      )

      setJsonData(prev => {
        const prevDatas = prev.datas || []
        const exists = prevDatas.some(
          item => String(item.id) === String(timeentryId)
        )
        if (!exists) return prev

        const updated = markTimeEntryAsReleased(prevDatas, timeentryId)

        setWeekLoggedHours(getWeekLoggedHours(updated))
        getTimeEntriesCount(updated)
        getWeekData(minDailyWorkingHours, updated, {
          preserveSelectedWeekDay: true,
        })

        return { ...prev, datas: updated }
      })
    },
    [
      getTimeEntriesCount,
      getWeekData,
      getWeekLoggedHours,
      minDailyWorkingHours,
      queryClient,
      setJsonData,
      setWeekLoggedHours,
    ]
  )

  const doRefreshEntryInGrid = useCallback(
    async (
      timeentryId: any,
      {
        removeIfFinalized = false,
        retainReleased = false,
        refreshTimelinePanel = false,
        removeOnError = false,
      }: any = {}
    ) => {
      try {
        const entryRes = await getTimeEntry(timeentryId)
        const entry = Array.isArray(entryRes) ? entryRes[0] : entryRes
        const hasSlots = entry?.slot_ids && entry.slot_ids.length > 0
        const isFinalized =
          removeIfFinalized &&
          (entry?.status === 'released' || entry?.approved_on != null)
        const shouldRetainReleased = retainReleased && entry != null
        let entrySlots: any[] = []

        if (hasSlots && !isFinalized && !retainReleased) {
          const slots = await getTimeSlots(
            `?timeentry_ids=${timeentryId}${getDelegatorsQuerySuffix()}`
          )
          entrySlots = slots || []
          slotsByEntryIdRef.current[timeentryId] = entrySlots
        } else {
          delete slotsByEntryIdRef.current[timeentryId]
        }

        setJsonData(prev => {
          const prevDatas = prev.datas || []
          const exists = prevDatas.some(
            item => String(item.id) === String(timeentryId)
          )

          let updated
          if ((hasSlots && !isFinalized) || shouldRetainReleased) {
            const normalized = normalizeTimeEntry(
              retainReleased ? { ...entry, status: 'released' } : { ...entry },
              minMinutes
            )
            updated = exists
              ? prevDatas.map(item =>
                  String(item.id) === String(timeentryId) ? normalized : item
                )
              : retainReleased
                ? prevDatas
                : [...prevDatas, normalized]
          } else {
            if (!exists) return prev
            updated = prevDatas.filter(
              item => String(item.id) !== String(timeentryId)
            )
          }

          setWeekLoggedHours(getWeekLoggedHours(updated))
          getTimeEntriesCount(updated)
          getWeekData(minDailyWorkingHours, updated, {
            preserveSelectedWeekDay: true,
          })

          return { ...prev, datas: updated }
        })

        if (refreshTimelinePanel) {
          setRefreshFlag(prev => prev + 1)
        }
      } catch (e) {
        console.error('[Webpush] refreshEntryInGrid failed:', e)

        if (removeOnError) {
          removeEntryFromGrid(timeentryId)
          if (refreshTimelinePanel) {
            setRefreshFlag(prev => prev + 1)
          }
        }
      }
    },
    [
      getDelegatorsQuerySuffix,
      getTimeEntriesCount,
      getWeekData,
      getWeekLoggedHours,
      minDailyWorkingHours,
      minMinutes,
      normalizeTimeEntry,
      removeEntryFromGrid,
      setJsonData,
      setRefreshFlag,
      setWeekLoggedHours,
    ]
  )

  const refreshEntryInGrid = useCallback(
    (timeentryId: any, options: any = {}) => {
      const key = String(timeentryId)
      const state = entryRefreshStateRef.current[key] || {}
      const needsExtra =
        Boolean(options.force) ||
        (options.removeIfFinalized && !state.lastOptions?.removeIfFinalized) ||
        (options.retainReleased && !state.lastOptions?.retainReleased) ||
        (options.removeOnError && !state.lastOptions?.removeOnError) ||
        (options.refreshTimelinePanel &&
          !state.lastOptions?.refreshTimelinePanel)

      if (state.promise) {
        if (needsExtra) {
          state.pending = { ...state.pending, ...options }
        }
        return state.promise
      }

      if (
        !needsExtra &&
        state.lastCompletedAt &&
        Date.now() - state.lastCompletedAt < 1000
      ) {
        return Promise.resolve()
      }

      const run = (runOptions: any): Promise<void> =>
        doRefreshEntryInGrid(timeentryId, runOptions).then(() => {
          const current = entryRefreshStateRef.current[key] || {}
          const next = current.pending

          if (next) {
            entryRefreshStateRef.current[key] = {
              promise: run(next),
              pending: null,
              lastOptions: runOptions,
            }
          } else {
            entryRefreshStateRef.current[key] = {
              promise: null,
              pending: null,
              lastOptions: runOptions,
              lastCompletedAt: Date.now(),
            }
          }
        })

      const promise = run(options)
      entryRefreshStateRef.current[key] = { ...state, promise, pending: null }
      return promise
    },
    [doRefreshEntryInGrid]
  )

  const findTimeEntryIdForSlot = useCallback(
    (slotId: any) => {
      for (const [entryId, slots] of Object.entries(
        slotsByEntryIdRef.current
      )) {
        if ((slots || []).some(slot => slot.id === slotId)) {
          return Number(entryId)
        }
      }

      const entry = (jsonData.datas || []).find(item =>
        (item.slot_ids || []).includes(slotId)
      )
      return entry ? entry.id : null
    },
    [jsonData.datas]
  )

  const handleWebpushObject = useCallback(
    async (object: any) => {
      if (!object?.action) return
      if (object.skipGridRefresh === true) return

      const action = String(object.action).toLowerCase()
      const type = String(object.type || '').toLowerCase()
      const relevantType = type === 'timeentry' || type === 'timeslot'
      const refreshOnClocking = [
        'clocking.start',
        'clocking.stop',
        'clocking.update',
        'clocking.updated',
        'clocking.delete',
        'clocking.deleted',
      ].includes(action)
      const refreshOnStatus =
        action.endsWith('.accept') ||
        action.endsWith('.accepted') ||
        action.endsWith('.release') ||
        action.endsWith('.released')
      const refreshOnObjectChange =
        relevantType &&
        (action.endsWith('.create') ||
          action.endsWith('.created') ||
          action.endsWith('.update') ||
          action.endsWith('.updated') ||
          action.endsWith('.delete') ||
          action.endsWith('.deleted'))
      const getRelatedTimeentryIds = () =>
        [
          ...(Array.isArray(object.stoppedTimeentryIds)
            ? object.stoppedTimeentryIds
            : []),
          ...(Array.isArray(object.relatedTimeentryIds)
            ? object.relatedTimeentryIds
            : []),
        ].filter(id => id != null)

      if (refreshOnClocking) {
        const timeentryId =
          type === 'timeentry' && object.id != null
            ? object.id
            : type === 'timeslot' && object.id != null
              ? findTimeEntryIdForSlot(object.id)
              : null

        const refreshIds = new Set(getRelatedTimeentryIds())
        if (timeentryId != null) refreshIds.add(timeentryId)

        if (refreshIds.size > 0) {
          const isClockingDelete =
            action === 'clocking.delete' || action === 'clocking.deleted'
          await Promise.all(
            Array.from(refreshIds).map(id =>
              refreshEntryInGrid(id, { removeOnError: isClockingDelete })
            )
          )
        } else {
          console.log(
            '[Webpush] clocking: cannot resolve timeentry, skip',
            object
          )
        }
        return
      }

      if (refreshOnStatus) {
        const timeentryId =
          type === 'timeentry'
            ? object.id
            : type === 'timeslot' && object.id != null
              ? findTimeEntryIdForSlot(object.id)
              : null

        if (timeentryId != null) {
          if (action.endsWith('.released')) {
            markEntryAsReleased(timeentryId)
            refreshEntryInGrid(timeentryId, { retainReleased: true })
          } else {
            refreshEntryInGrid(timeentryId, { removeIfFinalized: true })
          }
        } else {
          console.log(
            '[Webpush] accept/release: cannot resolve timeentry, skip',
            object
          )
        }
        return
      }

      if (refreshOnObjectChange) {
        const isDelete =
          action.endsWith('.delete') || action.endsWith('.deleted')
        const isCreate =
          action.endsWith('.create') || action.endsWith('.created')

        if (type === 'timeentry' && object.id != null) {
          if (isDelete) {
            removeEntryFromGrid(object.id)
          } else {
            refreshEntryInGrid(object.id, { force: isCreate })
          }
          return
        }

        if (type === 'timeslot' && object.id != null) {
          const timeentryId = findTimeEntryIdForSlot(object.id)
          if (timeentryId != null) {
            refreshEntryInGrid(timeentryId, { force: isCreate })
          } else {
            console.log(
              '[Webpush] update/delete: cannot resolve timeentry for slot, skip',
              object
            )
          }
        }
      }
    },
    [
      findTimeEntryIdForSlot,
      markEntryAsReleased,
      refreshEntryInGrid,
      removeEntryFromGrid,
    ]
  )

  useEffect(() => {
    getTimeEntriesCount(jsonData.datas)
  }, [filter, getTimeEntriesCount, jsonData.datas, selectedWeekDay, step])

  return {
    loadTimeEntries,
    updateTimeEntriesByDate,
    applyTimeEntryPatch,
    handleWebpushObject,
    setFilter,
  }
}
